/**
 * Armada About — reactive Flex Profile + Skill/Character segment scroll.
 *
 * The "Flex Profile" widget is driven by the variant-level metafield
 * `pim.flex_variant` (a metaobject) → `flex_profile`. Because the About section
 * is separate from the main product section, a size change does not re-render it
 * via the Section Rendering API. This script keeps the flex number + bar in sync
 * by reading a per-variant map embedded in the section and listening for the
 * theme's `variantChange` pub/sub event.
 *
 * Segmented rows (Character / Skill Level) auto-scroll the highlighted span
 * into view on mobile overflow (AAS-4933 / AAS-5099).
 *
 * See docs/pim-field-migration.md.
 */
(() => {
  'use strict';

  // Fill % aligns the bar edge to the rating on the 1–10 scale: (value - 1) / 9.
  const fillFor = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(100, ((n - 1) / 9) * 100));
  };

  // Boot flex rating fill on the 80–130 scale: (value - 80) / 50.
  const fillForRating = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.max(0, Math.min(100, ((n - 80) / 50) * 100));
  };

  const formatNumber = (value, mode) => {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    // 'rating' (boots) → whole number (e.g. 130); 'profile' (skis) → one decimal.
    return mode === 'rating' ? String(Math.round(n)) : n.toFixed(1);
  };

  const initGroup = (group) => {
    if (group.dataset.flexBound === 'true') return;
    group.dataset.flexBound = 'true';

    // Per-size flex map (embedded JSON). Optional: the reveal/count-up runs
    // without it — only variant reactivity needs the map.
    let map = {};
    let sizeIndex = 0;
    const dataEl = group.querySelector('script[type="application/json"][data-flex-variants]');
    if (dataEl) {
      try {
        map = JSON.parse(dataEl.textContent);
      } catch (e) {
        map = {};
      }
      // 1-based index of the "Size" option in variant.options; 0 = no Size
      // option (map is then keyed by variant id instead).
      sizeIndex = parseInt(dataEl.getAttribute('data-size-index') || '0', 10);
    }

    // 'rating' (boots): integer + green bar on the 80–130 scale.
    // 'profile' (skis): one-decimal + bar on the 1–10 scale.
    const mode = group.getAttribute('data-flex-mode') || 'profile';
    // The snowboard About section reuses this script with its own BEM namespace,
    // so the hide-when-empty class is overridable. Defaults to the ski class.
    const emptyClass = group.getAttribute('data-flex-empty-class') || 'armada-about__flex-group--empty';
    const numberEl = group.querySelector('[data-flex-number]');
    const barEl = group.querySelector('[data-flex-bar]');
    if (!numberEl || !barEl) return;

    // The server-rendered number is the count-up target. Blank → nothing to
    // animate (the group is hidden via --empty).
    const raw = (numberEl.textContent || '').trim();
    let target = raw === '' ? NaN : Number(raw);

    // Both About sections (ski + snowboard) reuse this script under their own BEM
    // namespaces, so the fill is marked with a data hook rather than a class:
    // its presence is the section's declaration that its CSS implements the
    // reveal (the is-anim-ready / is-flex-revealed rules). Gating on it keeps bar
    // and number animating together or not at all — never a half-animation.
    const animatableFill = barEl.querySelector('[data-flex-fill]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canAnimate = !reduce && 'IntersectionObserver' in window && !Number.isNaN(target) && !!animatableFill;
    // Until the bar is well into view we hold it collapsed with the number at 0;
    // once revealed the count-up has run and the number reflects the value.
    let revealed = !canAnimate;
    let rafId = null;

    const stopCount = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // easeOutCubic count from 0 → value, in step with the bar's grow.
    const countUp = (to) => {
      stopCount();
      const duration = 1000;
      let startTs = null;
      const frame = (ts) => {
        if (startTs === null) startTs = ts;
        const p = Math.min((ts - startTs) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        numberEl.textContent = formatNumber(to * eased, mode);
        if (p < 1) {
          rafId = requestAnimationFrame(frame);
        } else {
          rafId = null;
          numberEl.textContent = formatNumber(to, mode);
        }
      };
      rafId = requestAnimationFrame(frame);
    };

    // Reactive update on size change (colour never alters flex — looked up by
    // Size). Before reveal we only store the new target so the count-up lands on
    // it; after reveal we snap the number to the new value (the bar transitions).
    const apply = (value) => {
      if (value === null || value === undefined || value === '') {
        group.classList.add(emptyClass);
        return;
      }
      group.classList.remove(emptyClass);
      const fill = mode === 'rating' ? fillForRating(value) : fillFor(value);
      barEl.style.setProperty('--flex-fill', `${fill}%`);
      target = Number(value);
      if (revealed) {
        stopCount();
        numberEl.textContent = formatNumber(value, mode);
      }
    };

    // Grow the bar + count the number up the first time it is clearly in view.
    if (canAnimate) {
      barEl.classList.add('is-anim-ready'); // collapse fill to 0 (CSS)
      numberEl.textContent = formatNumber(0, mode);
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            revealed = true;
            // Defer a frame so the 0-width / 0-value start is committed first.
            requestAnimationFrame(() => {
              barEl.classList.add('is-flex-revealed');
            });
            countUp(target);
          });
        },
        // Fire only once the bar sits well inside the viewport (its top is above
        // the 75% line), not as its top edge first peeks in.
        { threshold: 0.2, rootMargin: '0px 0px -25% 0px' }
      );
      observer.observe(barEl);
    }

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      // The event fires from the main product section, so don't filter by
      // sectionId. Look up by SIZE so a colour-only change never alters flex.
      const variant = event?.data?.variant;
      if (!variant) return;
      const key = sizeIndex > 0 && Array.isArray(variant.options)
        ? variant.options[sizeIndex - 1]
        : variant.id;
      if (key == null || !(key in map)) return; // not a known size / variant
      apply(map[key]);
    });
  };

  // Segmented rows (Character / Skill Level) become horizontal scroll strips
  // under ~495px. Toggle classes so the CSS edge-fade only shows on the side
  // that still has content scrolled off (see armada-about.css). Widths are
  // handled in CSS (min-width per item); this is purely the fade indicator.
  // Harmless above the breakpoint.
  //
  // Auto-scrolls the highlighted (active) span into view on overflow — e.g. an
  // "expert"-only skill level sitting off the right edge on mobile. Layout is
  // retried via ResizeObserver + fonts.ready because the body-loaded stylesheet
  // often has not applied on the first paint (max === 0). The first time the
  // strip intersects the viewport we smooth-scroll so the motion is visible as
  // the shopper reaches About; later layout retries snap. A manual swipe wins.
  // AAS-4933 / AAS-5099.
  const initSegmentScroll = (strip) => {
    if (strip.dataset.segmentScrollBound === 'true') return;
    strip.dataset.segmentScrollBound = 'true';

    let userScrolled = false;
    let programmatic = false;
    let progTimer = null;

    const update = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      const x = strip.scrollLeft;
      // Tolerances absorb sub-pixel rounding; when max <= 0 both are true → no fade.
      strip.classList.toggle('is-scroll-start', x <= 1);
      strip.classList.toggle('is-scroll-end', x >= max - 1);
      // Overflowing strips are keyboard-scrollable once focused; drop the
      // extra tab stop when the row fits (desktop / wide phones).
      if (max > 1) {
        strip.tabIndex = 0;
      } else {
        strip.removeAttribute('tabindex');
      }
    };

    // Fade inset matches the strip's side padding (--space-7 on mobile).
    const fadePx = () => parseFloat(getComputedStyle(strip).paddingLeft) || 0;

    // Center the full active span (multi-value lists stay one range), then
    // nudge so the span is not sitting under the edge-fade mask. Clamp to
    // [0, max]: beginner-only stays at 0; expert-only lands at the right end.
    const computeTarget = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      if (max <= 0) return null;
      const actives = strip.querySelectorAll('.armada-about__segment--active');
      if (!actives.length) return null;
      const stripRect = strip.getBoundingClientRect();
      const firstRect = actives[0].getBoundingClientRect();
      const lastRect = actives[actives.length - 1].getBoundingClientRect();
      const spanLeft = firstRect.left - stripRect.left + strip.scrollLeft;
      const spanWidth = lastRect.right - firstRect.left;
      const spanRight = spanLeft + spanWidth;
      const view = strip.clientWidth;
      let target = Math.max(0, Math.min(max, spanLeft - (view - spanWidth) / 2));

      const fade = fadePx();
      if (fade > 0) {
        const atStart = target <= 1;
        const atEnd = target >= max - 1;
        const insetLeft = atStart ? 0 : fade;
        const insetRight = atEnd ? 0 : fade;
        const paddedLeft = target + insetLeft;
        const paddedRight = target + view - insetRight;
        if (spanLeft < paddedLeft) {
          target = spanLeft - insetLeft;
        } else if (spanRight > paddedRight) {
          target = spanRight + insetRight - view;
        }
        target = Math.max(0, Math.min(max, target));
      }
      return target;
    };

    const finishProgrammatic = () => {
      if (progTimer) {
        window.clearTimeout(progTimer);
        progTimer = null;
      }
      programmatic = false;
      update();
    };

    const scrollToTarget = (smooth) => {
      if (userScrolled) return;
      // Don't let a layout retry snap-kill an in-flight smooth scroll.
      if (programmatic && !smooth) return;
      const target = computeTarget();
      if (target == null) {
        update();
        return;
      }
      if (Math.abs(strip.scrollLeft - target) < 1) {
        update();
        return;
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const useSmooth = smooth && !reduce && typeof strip.scrollTo === 'function';
      programmatic = true;
      if (useSmooth) {
        strip.scrollTo({ left: target, behavior: 'smooth' });
        strip.addEventListener('scrollend', finishProgrammatic, { once: true });
        if (progTimer) window.clearTimeout(progTimer);
        progTimer = window.setTimeout(finishProgrammatic, 500);
      } else {
        strip.scrollLeft = target;
        programmatic = false;
        update();
      }
    };

    const onLayout = () => {
      update();
      if (!programmatic) scrollToTarget(false);
    };

    strip.addEventListener('scroll', () => {
      if (!programmatic) userScrolled = true;
      update();
    }, { passive: true });

    // Let a finger/wheel take over if they interrupt the programmatic scroll.
    const onUserIntent = () => {
      if (!programmatic) return;
      strip.removeEventListener('scrollend', finishProgrammatic);
      finishProgrammatic();
      userScrolled = true;
    };
    strip.addEventListener('touchstart', onUserIntent, { passive: true });
    strip.addEventListener('wheel', onUserIntent, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(onLayout);
      ro.observe(strip);
    } else {
      window.addEventListener('resize', onLayout);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        requestAnimationFrame(onLayout);
      });
    }

    requestAnimationFrame(onLayout);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          scrollToTarget(true);
        });
      }, { threshold: 0.2 });
      io.observe(strip);
    }
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-flex-group]').forEach(initGroup);
    root.querySelectorAll('.armada-about__segments').forEach(initSegmentScroll);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (!e.target) return;
    init(e.target);
  });
})();
